import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1OrgProductsId,
  deleteAdminV1OrgProductsIdVariantsVariantId,
  getAdminV1OrgProducts,
  getAdminV1OrgProductsId,
  getAdminV1OrgProductsIdMedia,
  getAdminV1OrgProductsIdRelations,
  getAdminV1OrgProductsIdSkuHistory,
  getAdminV1OrgProductsIdSkuPreview,
  getAdminV1OrgProductsIdVariants,
  getAdminV1OrgProductsHealth,
  getAdminV1OrgProductsVendors,
  getAdminV1OrgSkuRulesResolve,
  getStoneSetting,
  patchAdminV1OrgProductsId,
  patchAdminV1OrgProductsIdVariants,
  patchAdminV1OrgProductsIdVariantsVariantId,
  postAdminV1OrgProducts,
  postAdminV1OrgProductsBulk,
  postAdminV1OrgProductsIdDuplicate,
  postAdminV1OrgProductsIdRegenerateSkus,
  postAdminV1OrgProductsIdVariants,
  putAdminV1OrgProductsIdMedia,
  putAdminV1OrgProductsIdRelations,
  putStoneSetting,
  deleteStoneSetting,
  type BulkProductsRequest,
  type BulkResponse,
  type DuplicateProductRequest,
  type ProductListPage,
  type PatchAdminV1OrgProductsIdBody,
  type PatchAdminV1OrgProductsIdVariantsVariantIdBody,
  type UpdateVariantsRequestItemsItem,
  type PostAdminV1OrgProductsBody,
  type PostAdminV1OrgProductsIdVariantsBody,
  type Product,
  type ProductFacetList,
  type ProductHealth,
  type ProductMediaItem,
  type ProductSaveResult,
  type ProductRelation,
  type ProductSkuPreview,
  type PutAdminV1OrgProductsIdMediaBody,
  type PutAdminV1OrgProductsIdRelationsBody,
  type PutStoneSettingRequest,
  type RegenerateSkusRequest,
  type RegenerateSkusResponse,
  type SkuHistoryItem,
  type SkuRuleResolve,
  type StoneSetting,
  type Variant,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`status=draft,active`, `tag=…`, `updatedAt_gte=2026-09-01`), `cursor`, `limit`.
 */
export type ProductListParams = ListParams;

/** API layer: thin, typed wrappers over the generated client for the organisation master catalog. */
export const productsApi = {
  list: (params: ProductListParams): Promise<ProductListPage> => getAdminV1OrgProducts(params),

  health: (): Promise<ProductHealth> => getAdminV1OrgProductsHealth(),
  get: (productId: string): Promise<Product> => getAdminV1OrgProductsId({ id: productId }),
  create: (input: PostAdminV1OrgProductsBody): Promise<ProductSaveResult> =>
    postAdminV1OrgProducts(input),
  update: (productId: string, input: PatchAdminV1OrgProductsIdBody): Promise<ProductSaveResult> =>
    patchAdminV1OrgProductsId({ id: productId }, input),
  remove: (productId: string, options?: { removeFromStores?: boolean }): Promise<void> =>
    deleteAdminV1OrgProductsId({ id: productId }, options),
  duplicate: (productId: string, body: DuplicateProductRequest): Promise<Product> =>
    postAdminV1OrgProductsIdDuplicate({ id: productId }, body),
  /**
   * 200 when every product succeeded, 207 when some did not. An id list answers one result per
   * id; a `selection` answers the failures only, with `matched`.
   */
  bulk: (body: BulkProductsRequest): Promise<BulkResponse> => postAdminV1OrgProductsBulk(body),
  /** Vendors already on the organisation's products, most used first; `q` matches anywhere in the name. */
  vendors: (q: string): Promise<ProductFacetList['items']> =>
    getAdminV1OrgProductsVendors({ ...(q ? { q } : {}), limit: 50 }).then((r) => r.items),

  listVariants: (productId: string): Promise<Variant[]> =>
    getAdminV1OrgProductsIdVariants({ id: productId }).then((r) => r.items),
  createVariant: (
    productId: string,
    body: PostAdminV1OrgProductsIdVariantsBody,
  ): Promise<Variant> => postAdminV1OrgProductsIdVariants({ id: productId }, body),
  updateVariant: (
    productId: string,
    variantId: string,
    body: PatchAdminV1OrgProductsIdVariantsVariantIdBody,
  ): Promise<Variant> =>
    patchAdminV1OrgProductsIdVariantsVariantId({ id: productId, variantId }, body),
  /** The variant table's save: changed rows only, one transaction; answers every variant. */
  updateVariants: (
    productId: string,
    items: UpdateVariantsRequestItemsItem[],
  ): Promise<Variant[]> =>
    patchAdminV1OrgProductsIdVariants({ id: productId }, { items }).then((r) => r.items),
  removeVariant: (productId: string, variantId: string): Promise<void> =>
    deleteAdminV1OrgProductsIdVariantsVariantId({ id: productId, variantId }),

  // SKU rules (D-161): what the rule would write for this product, the write itself, and the
  // values the product and its variants carried before.
  skuPreview: (productId: string, includeManual: boolean): Promise<ProductSkuPreview> =>
    getAdminV1OrgProductsIdSkuPreview({ id: productId }, includeManual ? { includeManual } : {}),
  regenerateSkus: (
    productId: string,
    body: RegenerateSkusRequest,
  ): Promise<RegenerateSkusResponse> =>
    postAdminV1OrgProductsIdRegenerateSkus({ id: productId }, body),
  skuHistory: (productId: string): Promise<SkuHistoryItem[]> =>
    getAdminV1OrgProductsIdSkuHistory({ id: productId }).then((r) => r.items),
  /** The rule a product of this type would get, with a sample style number; no type means the default rule. */
  resolveSkuRule: (productTypeId: string | null): Promise<SkuRuleResolve> =>
    getAdminV1OrgSkuRulesResolve(productTypeId ? { productTypeId } : {}),

  getMedia: (productId: string): Promise<ProductMediaItem[]> =>
    getAdminV1OrgProductsIdMedia({ id: productId }).then((r) => r.items),
  setMedia: (
    productId: string,
    body: PutAdminV1OrgProductsIdMediaBody,
  ): Promise<ProductMediaItem[]> =>
    putAdminV1OrgProductsIdMedia({ id: productId }, body).then((r) => r.items),

  getRelations: (productId: string): Promise<ProductRelation[]> =>
    getAdminV1OrgProductsIdRelations({ id: productId }).then((r) => r.items),
  setRelations: (
    productId: string,
    body: PutAdminV1OrgProductsIdRelationsBody,
  ): Promise<ProductRelation[]> =>
    putAdminV1OrgProductsIdRelations({ id: productId }, body).then((r) => r.items),

  // A product is a diamond setting only when it has a stone-setting; a 404 means it is a plain
  // product, which the editor shows as the toggle being off.
  getConfigurator: (productId: string): Promise<StoneSetting | null> =>
    getStoneSetting({ productId }).catch((error: unknown) => {
      if (isApiError(error) && error.code === 'NOT_FOUND') return null;
      throw error;
    }),
  setConfigurator: (productId: string, body: PutStoneSettingRequest): Promise<StoneSetting> =>
    putStoneSetting({ productId }, body),
  removeConfigurator: (productId: string): Promise<void> => deleteStoneSetting({ productId }),
};
